import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { MorphingOrb } from "@/components/PremiumAnimations";
import { industries } from "@/data/industries";
import SEOHead from "@/components/SEOHead";

export default function IndustriesPage() {
  return (
    <main data-testid="industries-page">
      <SEOHead
        title="Industries | Deep Domain Expertise — Venury"
        description="We build technology for industries where getting it wrong isn't an option. FinTech, trading, payments, digital assets, SaaS, AI, and more."
        url="https://venury.tech/industries"
      />
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <MorphingOrb
          color1="rgba(22, 160, 133, 0.15)"
          color2="rgba(72, 201, 176, 0.08)"
          size={500}
          duration={25}
          style={{ top: "-10%", right: "-10%" }}
        />
        <MorphingOrb
          color1="rgba(230, 126, 34, 0.08)"
          color2="rgba(211, 84, 0, 0.05)"
          size={350}
          duration={30}
          style={{ bottom: "0%", left: "-5%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B1116]/50 to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <span className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em]">
              Industries
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight max-w-4xl"
          >
            Deep Domain Expertise Where It <span className="gradient-text-teal">Matters Most</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mt-8"
          >
            We don't build generic software. We build technology for industries where 
            getting it wrong isn't an option. Financial services, trading, payments, 
            and digital assets.
          </motion.p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1}>
            {industries.map((industry) => {
              const Icon = industry.icon;
              return (
                <StaggerItem key={industry.slug}>
                  <Link
                    to={`/industries/${industry.slug}`}
                    data-testid={`industry-card-${industry.slug}`}
                    className="block glass-card rounded-lg p-8 group hover:border-teal-primary/30 transition-all duration-500 h-full flex flex-col relative overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-teal-primary/5 rounded-bl-full transition-all duration-500 group-hover:w-40 group-hover:h-40 group-hover:bg-teal-primary/10" />
                    
                    <div className="relative z-10">
                      <div className="w-14 h-14 rounded-xl bg-teal-primary/10 flex items-center justify-center mb-6 group-hover:bg-teal-primary/20 transition-colors duration-300">
                        <Icon size={26} className="text-teal-bright" />
                      </div>

                      <h3 className="font-heading text-xl font-bold text-white mb-3 group-hover:text-teal-bright transition-colors duration-300">
                        {industry.title}
                      </h3>

                      <p className="text-sm text-slate-400 leading-relaxed mb-6">
                        {industry.shortDesc}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-6">
                        {industry.stats.slice(0, 2).map((stat, j) => (
                          <span key={j} className="font-mono text-[10px] uppercase tracking-wider bg-teal-primary/10 text-teal-bright px-3 py-1 rounded">
                            {stat}
                          </span>
                        ))}
                      </div>

                      <span className="inline-flex items-center gap-2 font-mono text-xs text-teal-bright uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                        Explore <ArrowRight size={12} />
                      </span>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>
    </main>
  );
}
